import { XClose } from '@untitledui/icons'
import { useEffect, useMemo } from 'react'

import { type WorkflowResponse } from '@state/workflow.ts'

import { formatDuration, formatDurationMs } from './util'

interface WorkflowInspectorProps {
  workflow: WorkflowResponse
  onClose: () => void
}

function WorkflowInspector({ workflow, onClose }: WorkflowInspectorProps) {
  const jobs = workflow.jobs

  // Calculate total time between jobs
  const totalTimeBetweenJobs = useMemo(() => {
    if (jobs.length < 2) return 0

    let totalMs = 0
    for (let i = 0; i < jobs.length - 1; i++) {
      const currentJob = jobs[i]
      const nextJob = jobs[i + 1]

      if (currentJob?.completedAt && nextJob?.startedAt) {
        const gapMs = new Date(nextJob.startedAt).getTime() - new Date(currentJob.completedAt).getTime()
        if (gapMs > 0) {
          totalMs += gapMs
        }
      }
    }

    return totalMs
  }, [jobs])

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString([], {
      dateStyle: 'short',
      timeStyle: 'medium',
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'RUNNING':
        return 'bg-blue-100 text-blue-800 border-blue-300'
      case 'COMPLETED':
        return 'bg-green-100 text-green-800 border-green-300'
      case 'FAILED':
        return 'bg-red-100 text-red-800 border-red-300'
      case 'PENDING':
        return 'bg-gray-100 text-gray-800 border-gray-300'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300'
    }
  }

  return (
    <div className="fixed inset-y-4 right-4 flex w-80 flex-col gap-4 rounded-2xl border bg-[white] p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <h3 className="mb-1 text-lg font-semibold">Workflow Details</h3>
          <p className="text-sm wrap-break-word text-secondary">
            {workflow.name ?? 'Unnamed Workflow'}
          </p>
        </div>
        <button
          onClick={onClose}
          className="shrink-0 rounded-sm p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
          aria-label="Close workflow inspector"
        >
          <XClose className="size-5" strokeWidth="2" />
        </button>
      </div>

      <div className="space-y-2 text-sm">
        <div>
          <span className="text-secondary">Status:</span>
          <div className="mt-1">
            <span className={`
              inline-block rounded-sm border px-2 py-1 text-xs font-medium
              ${getStatusColor(workflow.status)}
            `}>
              {workflow.status}
            </span>
          </div>
        </div>

        <div>
          <span className="text-secondary">Started:</span>
          <div className="mt-1">{formatDateTime(workflow.createdAt)}</div>
        </div>

        {workflow.status !== 'RUNNING' && (
          <div>
            <span className="text-secondary">Completed:</span>
            <div className="mt-1">{formatDateTime(workflow.updatedAt)}</div>
          </div>
        )}

        <div>
          <span className="text-secondary">Duration:</span>
          <div className="mt-1">
            {formatDuration(workflow.createdAt, workflow.updatedAt)}
          </div>
        </div>

        {totalTimeBetweenJobs > 0 && (
          <div>
            <span className="text-secondary">Time between jobs:</span>
            <div className="mt-1">
              {formatDurationMs(totalTimeBetweenJobs)}
            </div>
          </div>
        )}

        {workflow.errorMessage && (
          <div>
            <span className="text-secondary">Error:</span>
            <div className="mt-1 text-xs wrap-break-word text-red-600">
              {workflow.errorMessage}
            </div>
          </div>
        )}
      </div>

      <div className="flex-1">
        <h4 className="mb-3 text-sm font-semibold">
            Jobs ({jobs.length})
        </h4>

        {jobs.length === 0 ? (
          <p className="text-sm text-secondary">No jobs found</p>
        ) : (
          <div className="space-y-2 overflow-y-auto">
            {jobs.map((job, index) => {
              // Calculate time between previous job completion and this job start
              const previousJob = index > 0 ? jobs[index - 1] : null
              const timeBetweenJobs = previousJob?.completedAt && job.startedAt
                ? new Date(job.startedAt).getTime() - new Date(previousJob.completedAt).getTime()
                : null

              return (
                <div key={job.id}>
                  {timeBetweenJobs !== null && timeBetweenJobs > 0 && (
                    <div className="mb-1 flex items-center gap-1 text-xs text-secondary italic">
                      <span className="text-orange-600">⏱</span>
                      <span>Wait time: {formatDurationMs(timeBetweenJobs)}</span>
                    </div>
                  )}
                  <div className="space-y-1 rounded-sm border p-2 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-secondary">
                          #{index + 1}
                      </span>
                      <span
                        className={`
                          rounded-sm px-1.5 py-0.5 text-xs font-medium
                          ${getStatusColor(
                  job.status
                )}
                        `}
                      >
                        {job.status}
                      </span>
                    </div>
                    <div className="font-medium wrap-break-word">{job.jobName}</div>
                    {job.startedAt && (
                      <div className="text-secondary">
                          Started: {formatDateTime(job.startedAt)}
                      </div>
                    )}
                    {job.completedAt && (
                      <div className="text-secondary">
                          Completed: {formatDateTime(job.completedAt)}
                      </div>
                    )}
                    {job.startedAt && job.completedAt && (
                      <div className="text-secondary">
                          Duration: {formatDuration(job.startedAt, job.completedAt)}
                      </div>
                    )}
                    {job.errorMessage && (
                      <div className="wrap-break-word text-red-600">
                        {job.errorMessage}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default WorkflowInspector